import { useMemo } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { formatters, humanizeSeconds } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { ContentLoader, Truncated } from '@frontend/design-system';
import { useFetchAggregatedCallsSummary } from '../hooks/use-fetch-aggregated-calls-summary';

interface Props {
  type: 'callDuration' | 'callVolume';
}

interface DataBlock {
  label: string;
  value: number | string;
}

export const SmallModePanelSummary = ({ type }: Props) => {
  const { t } = useTranslation('analytics-phone');
  const { isLoading, ...rest } = useFetchAggregatedCallsSummary();

  const data: DataBlock[] = useMemo(() => {
    switch (type) {
      case 'callDuration':
        return [
          {
            label: t('Avg Call Duration'),
            value: humanizeSeconds(rest.data?.callDuration?.avg),
          },
          {
            label: t('Long Duration Calls'),
            value: formatters.value(rest.data?.callDuration?.totalLongCalls),
          },
        ];

      case 'callVolume':
        return [
          {
            label: t('Call Answer rate'),
            value: `${(rest.data?.answerRate?.rate || 0).toFixed(2)}%`,
          },
          {
            label: t('Total Call Volume'),
            value: formatters.value(rest.data?.totalCalls?.total),
          },
        ];

      default:
        return [];
    }
  }, [rest, t, type]);

  return (
    <div css={styles.wrapper}>
      {data.map(({ label, value }) => {
        return (
          <div className='summary-item' key={label}>
            <Truncated className='label'>{label}</Truncated>
            <Truncated className='value'>{value}</Truncated>
          </div>
        );
      })}
      <ContentLoader show={isLoading} size='small' />
    </div>
  );
};

const styles = {
  wrapper: css`
    border-radius: ${theme.borderRadius.medium};
    border: 1px solid ${theme.colors.neutral20};
    display: flex;
    gap: ${theme.spacing(3)};
    justify-content: space-between;
    margin-bottom: ${theme.spacing(2)};
    overflow: hidden;
    padding: ${theme.spacing(2)};
    position: relative;

    .summary-item {
      flex: 1;
      max-width: calc(50% - ${theme.spacing(1)});
      text-align: center;

      .label {
        color: ${theme.font.colors.subdued};
        font-size: ${theme.font.size.medium};
        font-weight: ${theme.font.weight.bold};
      }

      .value {
        color: ${theme.font.colors.default};
        font-size: ${theme.font.size.h2};
        font-weight: ${theme.font.weight.bold};
      }
    }
  `,
};
